import { c as _c } from "react/compiler-runtime";
import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";
import { selectCallstack } from "#/features/callstack/model/callstackSlice";
import { getPlaybackStepGroups, getPlaybackStepIndex } from "#/features/treeViewer/lib";
import { TabContentScrollContainer } from "#/shared/ui/templates/TabContentScrollContainer";
import { useAppSelector } from "#/store/hooks";
import { CallstackArgumentsValue, CallstackNodeBadge } from "./CallstackFrameContent";
type CompactCallstackListProps = {
  height?: number | string;
};
const COMPACT_CALLSTACK_ROW_HEIGHT = 58;
export const CompactCallstackList: React.FC<CompactCallstackListProps> = t0 => {
  const $ = _c(31);
  const {
    height: t1
  } = t0;
  const height = t1 === undefined ? 262 : t1;
  const theme = useTheme();
  const callstack = useAppSelector(selectCallstack);
  const runtimeErrorMessage = callstack.error?.message ?? null;
  const containerRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const rowRefs = useRef(t2);
  let t3;
  if ($[1] !== callstack.frames) {
    t3 = getPlaybackStepGroups(callstack.frames);
    $[1] = callstack.frames;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const playbackStepGroups = t3;
  const activePlaybackStepIndex = getPlaybackStepIndex(playbackStepGroups, callstack.frameIndex);
  let t4;
  let t5;
  if ($[3] !== activePlaybackStepIndex || $[4] !== playbackStepGroups) {
    t4 = () => {
      if (activePlaybackStepIndex < 0) {
        return;
      }
      const activeGroup = playbackStepGroups[activePlaybackStepIndex];
      if (!activeGroup) {
        return;
      }
      const container = containerRef.current;
      const activeRow = rowRefs.current[activeGroup.key];
      if (!container || !activeRow) {
        return;
      }
      const targetScrollTop = activeRow.offsetTop - (container.clientHeight - activeRow.offsetHeight) / 2;
      const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
      const nextScrollTop = Math.min(Math.max(targetScrollTop, 0), maxScrollTop);
      container.scrollTo({
        top: nextScrollTop,
        behavior: "smooth"
      });
    };
    t5 = [activePlaybackStepIndex, playbackStepGroups];
    $[3] = activePlaybackStepIndex;
    $[4] = playbackStepGroups;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  if (!callstack.isReady || playbackStepGroups.length === 0) {
    let t6;
    if ($[7] !== height) {
      t6 = {
        height
      };
      $[7] = height;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        px: 1.5,
        py: 1.25,
        minHeight: "100%",
        display: "flex",
        alignItems: "center"
      };
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    const t8 = runtimeErrorMessage ? "error.main" : "text.secondary";
    const t9 = runtimeErrorMessage ? "alert" : undefined;
    const t10 = runtimeErrorMessage ? `Preview runtime failed: ${runtimeErrorMessage}` : "Runtime actions will appear here once the preview is ready.";
    let t11;
    if ($[10] !== t10 || $[11] !== t8 || $[12] !== t9) {
      t11 = <Box sx={t7}><Typography variant="caption" color={t8} role={t9}>{t10}</Typography></Box>;
      $[10] = t10;
      $[11] = t8;
      $[12] = t9;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    let t12;
    if ($[14] !== t11 || $[15] !== t6) {
      t12 = <TabContentScrollContainer ref={containerRef} defer={true} style={t6}>{t11}</TabContentScrollContainer>;
      $[14] = t11;
      $[15] = t6;
      $[16] = t12;
    } else {
      t12 = $[16];
    }
    return t12;
  }
  let t6;
  if ($[17] !== height) {
    t6 = {
      height
    };
    $[17] = height;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      scrollbars: {
        autoHide: "leave",
        autoHideSuspend: false
      },
      overflow: {
        x: "hidden"
      }
    };
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      px: 1.5,
      py: 1.25
    };
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== activePlaybackStepIndex || $[22] !== callstack.frameIndex || $[23] !== playbackStepGroups || $[24] !== theme) {
    t9 = playbackStepGroups.map((group, index) => {
      const isActive = index === activePlaybackStepIndex;
      const isPast = activePlaybackStepIndex >= 0 && group.endIndex < callstack.frameIndex;
      const frame = group.primaryFrame;
      const isSwapGroup = group.kind === "swap";
      return <div key={group.key} ref={node => {
        rowRefs.current[group.key] = node;
      }}><Box sx={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr)",
          gap: 1,
          alignItems: "center",
          px: 1,
          py: 0.75,
          minHeight: COMPACT_CALLSTACK_ROW_HEIGHT,
          borderRadius: 2,
          bgcolor: isActive ? alpha(theme.appDesign.accent, 0.14) : isPast ? alpha(theme.appDesign.accentSoft, 0.05) : "transparent"
        }}><Box sx={{
            minWidth: 24
          }}>{"treeName" in frame && "nodeId" in frame ? <CallstackNodeBadge treeName={frame.treeName} id={frame.nodeId} size={24} showTooltip={false} /> : null}</Box><Stack spacing={0.25} sx={{
            minWidth: 0
          }}><Typography variant="caption" sx={{
              color: isActive ? theme.appDesign.accentSoft : "text.secondary",
              fontWeight: isActive ? 700 : 500
            }}>{String(index + 1).padStart(2, "0")}{" "}{isSwapGroup ? "swapChildren" : frame.name}</Typography><Box sx={{
              color: "text.secondary",
              typography: "caption",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minHeight: 18
            }}>{isSwapGroup ? <Stack direction="row" spacing={0.5} alignItems="center" sx={{
                minWidth: 0,
                whiteSpace: "nowrap"
              }}><Typography component="span" variant="inherit">L:</Typography><CallstackArgumentsValue frame={group.primaryFrame} showTooltip={false} /><Typography component="span" variant="inherit">R:</Typography><CallstackArgumentsValue frame={group.partnerFrame} showTooltip={false} /></Stack> : "args" in frame ? <CallstackArgumentsValue frame={frame} showTooltip={false} /> : "\xA0"}</Box></Stack></Box></div>;
    });
    $[21] = activePlaybackStepIndex;
    $[22] = callstack.frameIndex;
    $[23] = playbackStepGroups;
    $[24] = theme;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t9) {
    t10 = <Box sx={t8}><Stack spacing={0.75}>{t9}</Stack></Box>;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t6) {
    t11 = <TabContentScrollContainer ref={containerRef} defer={true} style={t6} options={t7}>{t10}</TabContentScrollContainer>;
    $[28] = t10;
    $[29] = t6;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
};